import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
export const frontmatter = {
  "title": "Thoughts on Future Branding",
  "slug": "thoughts-on-future-branding",
  "publishedAt": "2018-06-07T00:00:00.000Z",
  "readTime": "6 min read",
  "excerpt": "Exploring branding in the age of AI and exponential design.",
  "authorName": "Petri Lahdelma",
  "authorSlug": "petri-lahdelma",
  "mainImageUrl": "https://cdn.sanity.io/images/ai4cwr0g/digitaltableteur-blog/9460b2174dd7707b21408f68dfb03bcf63050d53-794x450.png?w=1600"
};
function _createMdxContent(props) {
  const _components = {
    blockquote: "blockquote",
    img: "img",
    p: "p",
    ..._provideComponents(),
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.p, {
      children: "Traditionally, an identity or brand has been a tool to help companies distinguish one (a product, an ideal, a set of values, or what the company does) from the other with numerous defining sub-genres and classifications, such as entry-level, iconic, luxury, etc. Usually consisting of a logo (logomark/logotype) and other graphic means designed to increase brand recognition."
    }), "\n", _jsx(_components.p, {
      children: "Brands have now adopted subtler tactics in retrospect to the early days. Brands tell stories on popular platforms, and in a subtle way that is nearly indistinguishable (advertorials) from the media readers already consume. By molding the message to the platform, brands can draw loyal customers and occasionally even make their content go viral. Tone of voice plays a big role in how brands are adopted by the masses. This encapsulates the general zeitgeist of branding today."
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "Brands have now adopted subtler tactics in retrospect to the early days."
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "In recent times, branding has evolved into various workflows and approaches, such as modular, responsive, interactive, or generative brand applications and corresponding identities, and vast variations of design systems to support and maintain the brand image. The craft itself has also somewhat evolved from promoting simple logomarks, and this has made the industry even more intricate."
    }), "\n", _jsx(_components.p, {
      children: "With advancements in machine learning, greater capacity for deeper neural networks (thanks to cloud solutions and Google’s efforts to speed up computing with GPUs), and especially in the field of algorithm-driven design, there’s no limit to the number of powerful tools we have access to. With the right tools and intellectual resources, we can apply the ideas garnered from the data and adapt to tomorrow's necessities, creating impactful design and successful content delivery, way “outside the box,” and soon beyond the capability of the human mind."
    }), "\n", _jsxs("figure", {
      children: [_jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "https://cdn.sanity.io/images/ai4cwr0g/digitaltableteur-blog/d1855849c569babe82244d83cf61a482938f278e-1000x200.webp?w=1600",
          alt: "."
        })
      }), _jsx("figcaption", {
        children: "A logotype refers to words or the name of a business that is designed or typeset."
      })]
    }), "\n", _jsxs("figure", {
      children: [_jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "https://cdn.sanity.io/images/ai4cwr0g/digitaltableteur-blog/232b37cc8479c31d1a2dd5bc9c34d56ab10ebe4c-1000x200.webp?w=1600",
          alt: "A collection of timeless logos."
        })
      }), _jsx("figcaption", {
        children: "A logomark is an identifying mark or symbol that doesn’t necessarily contain the business name."
      })]
    }), "\n", _jsx(_components.p, {
      children: "The technologies of tomorrow are bound to change how we design corporate identification systems and brand presence in the future. There’s no avoiding it, and the sci-fi geek in me can’t wait for news on yet another tech or gadget that will completely revolutionize the way we experience, exist, and interact."
    }), "\n", _jsx(_components.p, {
      children: "Thinking outside the box has always been the mantra of the creative industry. The truth is that more times than we’d like to admit, we’re being stuffed into that very box by our clients’ budget, our lack of vision, our fear of failure, or even sheer laziness. I’ve experienced many, if not all, possible pitfalls in my personal career as a designer, and in that sense, I am no better than my fellow designers. But where are the brands and identities that strive to do something more with the vast possibilities at our disposal today?"
    }), "\n", _jsxs("figure", {
      children: [_jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "https://cdn.sanity.io/images/ai4cwr0g/digitaltableteur-blog/69a7232767b490789f90493af72caebd96ea39fc-2000x1333.webp?w=1600",
          alt: "Programmatic logo that changes based parameters."
        })
      }), _jsx("figcaption", {
        children: "A philosophical brand that advocates living instinctively, responding in the moment, and letting change flow. The brand evokes this uniquely Chinese state of mind. By Wolff Olins."
      })]
    }), "\n", _jsx(_components.p, {
      children: "Genesis Beijing is a visual tool that explores each state and associated feeling through rich, reactive pattern. By Wolff Olins"
    }), "\n", _jsxs("figure", {
      children: [_jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "https://cdn.sanity.io/images/ai4cwr0g/digitaltableteur-blog/ce33d4637d29c1ad7f560cba96f96403118a9f6e-1920x1297.gif?w=1600",
          alt: "Ministry of Foreign Affairs Finland interactive logo"
        })
      }), _jsx("figcaption", {
        children: "Identity for Ministry for Foreign Affairs of Finland echoes the world events with changing color schemes. By 358"
      })]
    }), "\n", _jsxs("figure", {
      children: [_jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "https://cdn.sanity.io/images/ai4cwr0g/digitaltableteur-blog/754a86b027c0c3374e23b45a6edf2a8778de75b6-1000x511.webp?w=1600",
          alt: "New Things Co morphing logo."
        })
      }), _jsx("figcaption", {
        children: "The applications make great use of the possible variations of the globe that yields some lovely color combinations and compositions. By 358"
      })]
    }), "\n", _jsx(_components.p, {
      children: "An organisation that operates in a stable manner in a turbulent world and that works to make the world a better place. Video courtesy of the Ministry for Foreign Affairs of Finland and 358"
    }), "\n", _jsx(_components.p, {
      children: "By focusing on the individual experience instead of the user itself, we can easily draw out the factors that dictate how experiences are perceived. That said, we still like to pick and choose where we follow the more traditional HCD (Human-Centric Design) model, much like our own smörgåsbord for design. We validate design choices in a hive-mind manner, which is made possible by the lack of hierarchy and principles of self-governance within New Things Co."
    }), "\n", _jsx(_components.p, {
      children: "So what technologies could one consider as a designer in a branding or related project? Intelligent, algorithm-driven, reactive, or whatever the choice of tech, beneath the surface still lies an intelligent creative that understands both the given constraints and possibilities or has the capacity for evaluating machine-based output. But through matured systems of smart, tech-driven assets, these new brands will soon serve mankind a genuine, empathetic, positive, and lasting experience."
    }), "\n", _jsxs("figure", {
      children: [_jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "https://cdn.sanity.io/images/ai4cwr0g/digitaltableteur-blog/3451b1a6485defad0e50a90d488a88901c7cda7b-1400x463.webp?w=1600",
          alt: "Playful and dynamic puzzle piece logo."
        })
      }), _jsx("figcaption", {
        children: "Example of the New Things Co identity mutations. 2016. 2018."
      })]
    }), "\n", _jsxs("figure", {
      children: [_jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "https://cdn.sanity.io/images/ai4cwr0g/digitaltableteur-blog/49d3ffb3adfd8e7e03a51e3794d8f060d03b34ce-1400x902.webp?w=1600",
          alt: "Ministry of Foreign Affairs real-time status is depicted in the color of the logo."
        })
      }), _jsx("figcaption", {
        children: "The brandmark.io system has been trained with over 1.4 million images, instantly generating thousands of logo ideas based on user input."
      })]
    }), "\n", _jsx(_components.p, {
      children: "One could suggest an umbrella term “Exponentially-Driven Design” to collect all these technological advancements and bundle them to serve as an aid for design efforts. ED for short, meaning the computer-assisted, exponential advancements in the creative process. However we might call it, as creatives, we need to remember to step back and observe our habits and find smarter, new ways of working. In a word, adapt to changes in a rapidly changing digital landscape."
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "“Everything should be made as simple as possible, but not simpler.”"
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: ". Albert Einstein"
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
